/**
 * Constants for ManagePlatform and related components
 * الثوابت المستخدمة في إدارة المنصة
 */

// Store Types - Helper function for language support
export const getStoreTypeName = (type, lang = 'ar') => {
  const types = {
    phone_numbers: { ar: 'أرقام الهواتف', en: 'Phone Numbers' },
    real_estate: { ar: 'العقارات', en: 'Real Estate' },
    cars: { ar: 'السيارات', en: 'Cars' },
    car_plates: { ar: 'لوحات السيارات', en: 'Car Plates' },
    ecommerce: { ar: 'التجارة الإلكترونية', en: 'E-Commerce' },
    spare_parts: { ar: 'قطع الغيار', en: 'Spare Parts' },
    auto: { ar: 'السيارات', en: 'Auto' }
  };
  return types[type] ? types[type][lang] : type;
};

export const STORE_TYPES = {
  phone_numbers: 'أرقام الهواتف',
  real_estate: 'العقارات',
  cars: 'السيارات',
  car_plates: 'لوحات السيارات',
  ecommerce: 'التجارة الإلكترونية',
  spare_parts: 'قطع الغيار',
  auto: 'السيارات'
};

// أنواع الشركة
export const BUSINESS_TYPES = {
  personal: 'فردي',
  company: 'شركة'
};

// Store Status - Helper function for language support
export const getStoreStatusLabel = (status, lang = 'ar') => {
  const statusLabels = {
    pending: { ar: 'قيد المراجعة', en: 'Pending Review' },
    pending_review: { ar: 'قيد المراجعة', en: 'Pending Review' },
    needs_edit: { ar: 'يتطلب تعديل', en: 'Needs Edit' },
    resubmitted: { ar: 'معاد الإرسال', en: 'Resubmitted' },
    edited: { ar: 'تم التعديل', en: 'Edited' },
    active: { ar: 'مفعّل', en: 'Active' },
    rejected: { ar: 'مرفوض', en: 'Rejected' },
    modification_required: { ar: 'يتطلب تعديل', en: 'Modification Required' },
  };
  return statusLabels[status] ? statusLabels[status][lang] : status;
};

import { Clock, Edit3, RotateCcw, CheckCircle, XCircle, Ban } from 'lucide-react';

export const STORE_STATUS = {
  pending: { label: 'قيد المراجعة', labelEn: 'Pending Review', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  pending_review: { label: 'قيد المراجعة', labelEn: 'Pending Review', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  needs_edit: { label: 'يتطلب تعديل', labelEn: 'Needs Edit', color: 'bg-blue-500/20 text-blue-400', icon: Edit3 },
  resubmitted: { label: 'معاد الإرسال', labelEn: 'Resubmitted', color: 'bg-purple-500/20 text-purple-400', icon: RotateCcw },
  edited: { label: 'تم التعديل', labelEn: 'Edited', color: 'bg-purple-500/20 text-purple-400', icon: RotateCcw },
  active: { label: 'مفعّل', labelEn: 'Active', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  approved: { label: 'مفعّل', labelEn: 'Active', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  stopped: { label: 'متوقف', labelEn: 'Stopped', color: 'bg-amber-500/20 text-amber-400', icon: Ban },
  rejected: { label: 'مرفوض', labelEn: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  modification_required: { label: 'يتطلب تعديل', labelEn: 'Modification Required', color: 'bg-blue-500/20 text-blue-400', icon: Edit3 },
};

// دالة فتح ملفات PDF (تدعم base64 و URLs)
// دالة فتح المستندات (PDF أو صور) - تدعم base64 و URLs
export const openDocument = (documentData, documentName = 'document') => {
  if (!documentData) return;
  
  // إذا كان base64 data URL
  if (documentData.startsWith('data:')) {
    try {
      // استخراج نوع الملف من data URL
      const mimeMatch = documentData.match(/data:([^;]+);/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      
      // استخراج البيانات من base64
      const base64Data = documentData.split('base64,')[1];
      
      // تحويل base64 إلى Blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      // فتح في نافذة جديدة
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // تنظيف URL بعد فترة
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      console.error('Error opening document:', err);
      // fallback: فتح مباشرة
      window.open(documentData, '_blank');
    }
  } else {
    // URL عادي
    window.open(documentData, '_blank');
  }
};

// دالة التحقق من نوع الملف
export const isPdfDocument = (doc) => {
  if (!doc) return false;
  return doc.toLowerCase().endsWith('.pdf') || 
         doc.includes('application/pdf') || 
         doc.includes('data:application/pdf');
};

// دالة التحقق إذا كان المستند صورة
export const isImageDocument = (doc) => {
  if (!doc) return false;
  return doc.includes('data:image/') || 
         doc.match(/\.(jpg|jpeg|png|gif|webp)$/i);
};
